import {
  clearStoredAccessToken,
  getStoredAccessToken,
  setStoredAccessToken,
} from "@/lib/api/auth-storage";

export type ApiErrorPayload = {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
};

export type ApiSuccessPayload<T> = {
  success: true;
  data: T;
};

export class ApiClientError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let refreshPromise: Promise<string | null> | null = null;
const AUTH_REFRESH_TIMEOUT_MS = 8000;

function createTimeoutSignal(timeoutMs: number): AbortSignal | undefined {
  if (typeof AbortSignal === "undefined" || typeof AbortSignal.timeout !== "function") {
    return undefined;
  }
  return AbortSignal.timeout(timeoutMs);
}

async function parseJsonResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new ApiClientError("Unexpected response from server", response.status);
  }
  return response.json();
}

async function tryRefreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    let res: Response;
    try {
      res = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        signal: createTimeoutSignal(AUTH_REFRESH_TIMEOUT_MS),
      });
    } catch {
      clearStoredAccessToken();
      return null;
    }

    if (!res.ok) {
      clearStoredAccessToken();
      return null;
    }

    const payload = (await parseJsonResponse(res)) as ApiSuccessPayload<{
      accessToken: string;
      accessTokenExpiresIn: number;
      user: unknown;
    }>;

    if (!payload?.success || !payload?.data?.accessToken) {
      clearStoredAccessToken();
      return null;
    }

    setStoredAccessToken(payload.data.accessToken);
    return payload.data.accessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

type ApiFetchOptions = RequestInit & {
  auth?: boolean;
  retryOnAuthFailure?: boolean;
};

export async function apiFetch<T>(
  input: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const { auth = false, retryOnAuthFailure = true, headers, ...rest } = options;

  const requestHeaders = new Headers(headers ?? {});
  const isFormDataBody =
    typeof FormData !== "undefined" && rest.body instanceof FormData;
  if (!requestHeaders.has("Content-Type") && rest.body && !isFormDataBody) {
    requestHeaders.set("Content-Type", "application/json");
  }

  if (auth) {
    const token = getStoredAccessToken();
    if (token) {
      requestHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  const response = await fetch(input, {
    cache: "no-store",
    ...rest,
    credentials: "include",
    headers: requestHeaders,
  });

  if (response.status === 401 && auth && retryOnAuthFailure) {
    const refreshedToken = await tryRefreshAccessToken();
    if (refreshedToken) {
      requestHeaders.set("Authorization", `Bearer ${refreshedToken}`);
      return apiFetch<T>(input, {
        ...rest,
        headers: requestHeaders,
        auth,
        retryOnAuthFailure: false,
      });
    }
  }

  const payload = await parseJsonResponse(response) as any;

  if (!response.ok || !payload?.success) {
    const err = payload as ApiErrorPayload;
    throw new ApiClientError(
      `${err?.error?.message ?? "Request failed"}`,
      response.status,
      err?.error?.code,
      err?.error?.details
    );
  }

  return (payload as ApiSuccessPayload<T>).data;
}

export async function refreshAuthToken(): Promise<string | null> {
  return tryRefreshAccessToken();
}
